import { useEffect, useRef, type ReactNode } from "react";
import css from "./AppShell.module.css";

/**
 * App-level layout primitive: sticky top zone (Header + optional secondary
 * navigation strips) over a scrollable main area, with the iOS safe-area
 * inset honoured at the bottom.
 *
 * Use this from every authenticated screen — wrap the screen content in
 * <AppShell header={…}>{content}</AppShell>. Modals, sheets, and the boot
 * splash bypass it.
 *
 * Multi-role users can change role two ways: tap the role badge in the header
 * (RoleMenu dropdown) or swipe horizontally anywhere on the shell.
 */
interface AppShellProps {
  /** Sticky header at the very top (B.2 wordmark + role-badge + date). */
  header: ReactNode;
  /** Optional secondary sticky strip(s) under the header (branch pills, sub-tabs). */
  topZone?: ReactNode;
  /** Main scrollable content. */
  children: ReactNode;
  /** Optional sticky footer (rare — most screens have no footer). */
  footer?: ReactNode;
  /** Viewport-lock (24.08): shell = ровно 100dvh, скроллит ВНУТРЕННИЙ контейнер
      экрана (.scroll-панели с pull-to-refresh), а не страница. Без пропа —
      прежний page-scroll: контент растит shell (канон OO/DC-экранов). */
  lockViewport?: boolean;
}

export function AppShell({ header, topZone, children, footer, lockViewport }: AppShellProps) {
  const shellRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  /* 27.08: высота закреплённой зоны → --app-top-h. Липкие элементы КОНТЕНТА
     (сводка дашборда и т.п.) обязаны липнуть ниже шапки+табов и под ними по
     z-index — иначе при скролле наезжают на шапку. Высота зоны переменная
     (range-календарь HR выше дневного), поэтому меряем, а не хардкодим. */
  useEffect(() => {
    const shell = shellRef.current, top = topRef.current;
    if (!shell || !top) return;
    /* 11.10: и в :root — окна-порталы (body), например «Проверка допработ», встают ПОД шапкой */
    const root = document.documentElement;
    const apply = () => { shell.style.setProperty("--app-top-h", `${top.offsetHeight}px`); root.style.setProperty("--app-top-h", `${top.offsetHeight}px`); };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(top);
    return () => { ro.disconnect(); root.style.removeProperty("--app-top-h"); };
  }, []);
  return (
    <div ref={shellRef} className={`${css.shell} ${lockViewport ? css.shellLock : ""}`}>
      <div ref={topRef} className={css.stickyTop}>
        {header}
        {topZone}
      </div>
      <main className={css.main}>{children}</main>
      {footer ? <div className={css.stickyBottom}>{footer}</div> : null}
    </div>
  );
}
